import { type KeyboardEvent, type ReactNode, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { Sheet as PhoneSheet } from "../phone/Sheet";
import { usePhone } from "../phone/usePhone";
import { IconButton } from "./Button";
import { cx } from "./cx";

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * The kit's sheet: a centred dialog on a laptop (`.scrim` + `.sheet`), and on a phone the app's own bottom sheet,
 * which can be pulled down and rides above the keyboard. Esc and the scrim close it; focus stays inside while it is
 * open and goes back to where it was when it closes.
 */
export function Sheet({
  open,
  onClose,
  title,
  sub,
  footer,
  wide = false,
  label,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  sub?: ReactNode;
  /** The buttons at the bottom, the main one last. */
  footer?: ReactNode;
  wide?: boolean;
  /** The dialog's name when the title is not plain text. */
  label?: string;
  children: ReactNode;
}) {
  const phone = usePhone();
  const box = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const close = useRef(onClose);
  close.current = onClose;

  // Why on the document too: a button that disables itself while it works takes focus with it, and a key press with
  // no focus inside the dialog never reaches its own handler, so Esc would stop working until a click.
  useEffect(() => {
    if (!open || phone) return;
    const lost = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented || box.current?.contains(document.activeElement)) return;
      e.stopPropagation();
      close.current();
    };
    document.addEventListener("keydown", lost);
    return () => document.removeEventListener("keydown", lost);
  }, [open, phone]);

  useEffect(() => {
    if (!open || phone) return;
    const before = document.activeElement as HTMLElement | null;
    const inside = FOCUSABLE.split(",").flatMap((f) => [`.sheet-b ${f}`, `.sheet-f ${f}`]).join(",");
    const first = box.current?.querySelector<HTMLElement>(inside);
    (first ?? box.current)?.focus({ preventScroll: true });
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
      before?.focus?.({ preventScroll: true });
    };
  }, [open, phone]);

  if (!open) return null;
  if (phone) {
    // Why the kit's own body and foot: the phone sheet draws edge to edge, so the side gutter comes with them.
    return (
      <PhoneSheet open={open} onClose={onClose} title={title} label={label} className="kitsheet" closable>
        <div className="sheet-b">
          {sub ? <p className="sheet-sub">{sub}</p> : null}
          {children}
        </div>
        {footer ? <div className="sheet-f">{footer}</div> : null}
      </PhoneSheet>
    );
  }

  const keys = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== "Tab" || !box.current) return;
    const all = [...box.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
    if (!all.length) return;
    const first = all[0];
    const last = all[all.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={box}
        className={cx("sheet", wide && "w640")}
        role="dialog"
        aria-modal="true"
        aria-labelledby={label ? undefined : titleId}
        aria-label={label}
        tabIndex={-1}
        onKeyDown={keys}
      >
        <div className="sheet-h">
          <div className="sp">
            <h3 id={titleId}>{title}</h3>
            {sub ? <p>{sub}</p> : null}
          </div>
          <IconButton icon="x" label="Close" onClick={onClose} />
        </div>
        <div className="sheet-b">{children}</div>
        {footer ? <div className="sheet-f">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
